import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Globe, ChevronDown, ChevronRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from './ui/dropdown-menu';

// Platform name constants
const PLATFORM_NAME = {
  en: "Executive Cockpit for Mining",
  pt: "Cockpit Executivo para a Mineração"
};

export const ExecutiveHeader = ({ year, onYearChange }) => {
  const { language, toggleLanguage } = useLanguage();
  const navigate = useNavigate();

  const menuItems = {
    operations: {
      label: { en: 'Operations', pt: 'Operações' },
      items: [
        { 
          label: { en: 'Operational Execution 2026', pt: 'Execução Operacional 2026' }, 
          path: '/operations', 
          section: 'overview-2026',
          hasSubmenu: true,
          submenu: [
            { label: { en: 'Overview', pt: 'Visão Geral' }, path: '/operations', section: 'overview-2026' },
            { label: { en: 'Mine & Ore Access', pt: 'Mina e Acesso ao Minério' }, path: '/operations', section: 'mina-acesso-2026' },
            { label: { en: 'Plant & Recovery', pt: 'Planta e Recuperação' }, path: '/operations', section: 'planta-recuperacao-2026' },
            { label: { en: 'Ore Stocks', pt: 'Stocks de Minério' }, path: '/operations', section: 'stocks-minerio-2026' },
            { label: { en: 'Production & Revenue', pt: 'Produção e Receita' }, path: '/operations', section: 'producao-receita-2026' },
            { label: { en: 'Operational Efficiency', pt: 'Eficiência Operacional' }, path: '/operations', section: 'eficiencia-operacional-2026' },
            { label: { en: 'Risk & Plan Delivery', pt: 'Risco e Entrega do Plano' }, path: '/operations', section: 'risco-entrega-2026' },
          ]
        },
        { 
          label: { en: 'Operational Review 2025', pt: 'Revisão Operacional 2025' }, 
          path: '/operations', 
          section: 'overview-2025',
          hasSubmenu: true,
          submenu: [
            { label: { en: 'Overview', pt: 'Visão Geral' }, path: '/operations', section: 'overview-2025' },
            { label: { en: 'Mine & Ore Access', pt: 'Mina e Acesso ao Minério' }, path: '/operations', section: 'mina-acesso-2025' },
            { label: { en: 'Plant & Recovery', pt: 'Planta e Recuperação' }, path: '/operations', section: 'planta-recuperacao-2025' },
            { label: { en: 'Ore Stocks', pt: 'Stocks de Minério' }, path: '/operations', section: 'stocks-minerio-2025' },
            { label: { en: 'Production & Revenue', pt: 'Produção e Receita' }, path: '/operations', section: 'producao-receita-2025' },
            { label: { en: 'Operational Efficiency', pt: 'Eficiência Operacional' }, path: '/operations', section: 'eficiencia-operacional-2025' },
            { label: { en: 'Operational Risk', pt: 'Risco Operacional' }, path: '/operations', section: 'risco-operacional-2025' },
          ]
        },
        { label: { en: 'Multi-Period Comparative', pt: 'Comparativo Multi-Período' }, path: '/operations', section: 'comparative' },
      ],
    },
    finance: {
      label: { en: 'Finance', pt: 'Finanças' },
      items: [
        { 
          label: { en: 'Budget Execution 2026', pt: 'Execução Orçamental 2026' }, 
          path: '/finance', 
          section: 'budget-execution',
          hasSubmenu: true,
          submenu: [
            { 
              label: { en: 'Income Statement', pt: 'Demonstração de Resultados' }, 
              path: '/finance', 
              section: 'income-statement-2026',
              hasSubmenu: true,
              submenu: [
                { label: { en: 'Revenue', pt: 'Receitas' }, path: '/finance', section: 'revenue-detail-2026' },
                { 
                  label: { en: 'Operating Costs', pt: 'Custos Operacionais' }, 
                  path: '/finance', 
                  section: 'opex-detail-2026',
                  hasSubmenu: true,
                  submenu: [
                    { label: { en: 'COGS', pt: 'CMVMC' }, path: '/finance', section: 'opex-cogs-2026' },
                    { label: { en: 'Personnel Costs', pt: 'Custos com Pessoal' }, path: '/finance', section: 'opex-personnel-2026' },
                    { label: { en: 'Subcontracts', pt: 'Subcontratos' }, path: '/finance', section: 'opex-subcontracts-2026' },
                    { label: { en: 'General & Administrative', pt: 'Gerais e Administrativos' }, path: '/finance', section: 'opex-fse-2026' },
                    { label: { en: 'Commercial Costs', pt: 'Custos de Comercialização' }, path: '/finance', section: 'opex-commercial-2026' },
                  ]
                },
                { label: { en: 'EBITDA', pt: 'EBITDA' }, path: '/finance', section: 'ebitda-detail-2026' },
                { label: { en: 'Non-Operating Results', pt: 'Resultados Não Operacionais' }, path: '/finance', section: 'non-operating-2026' },
                { label: { en: 'Financial Results', pt: 'Resultados Financeiros' }, path: '/finance', section: 'financial-results-2026' },
                { label: { en: 'Industrial Tax', pt: 'Imposto Industrial' }, path: '/finance', section: 'industrial-tax-2026' },
                { label: { en: 'Net Income', pt: 'Resultado Líquido' }, path: '/finance', section: 'netincome-detail-2026' },
                { label: { en: 'Unit Cost per Carat', pt: 'Custo Unitário por Quilate' }, path: '/finance', section: 'costpercarat-detail-2026' },
              ]
            },
            { label: { en: 'Liquidity & Cash Generation', pt: 'Liquidez e Geração de Caixa' }, path: '/finance', section: 'liquidity-2026' },
            { label: { en: 'Balance Sheet', pt: 'Balanço Patrimonial' }, path: '/finance', section: 'balance-sheet-2026' },
            { label: { en: 'Debt & Financing', pt: 'Dívida e Financiamento' }, path: '/finance', section: 'debt-2026' },
            { label: { en: 'CAPEX', pt: 'CAPEX' }, path: '/finance', section: 'capex-2026' },
          ]
        },
        { 
          label: { en: 'Financial Review 2025', pt: 'Revisão Financeira 2025' }, 
          path: '/finance', 
          section: 'performance',
          hasSubmenu: true,
          submenu: [
            { 
              label: { en: 'Income Statement', pt: 'Demonstração de Resultados' }, 
              path: '/finance', 
              section: 'income-statement',
              hasSubmenu: true,
              submenu: [
                { label: { en: 'Revenue', pt: 'Receitas' }, path: '/finance', section: 'revenue-detail' },
                { 
                  label: { en: 'Operating Costs', pt: 'Custos Operacionais' }, 
                  path: '/finance', 
                  section: 'opex-detail',
                  hasSubmenu: true,
                  submenu: [
                    { label: { en: 'COGS', pt: 'CMVMC' }, path: '/finance', section: 'opex-cogs' },
                    { label: { en: 'Personnel Costs', pt: 'Custos com Pessoal' }, path: '/finance', section: 'opex-personnel' },
                    { label: { en: 'Subcontracts', pt: 'Subcontratos' }, path: '/finance', section: 'opex-subcontracts' },
                    { label: { en: 'General & Administrative', pt: 'Gerais e Administrativos' }, path: '/finance', section: 'opex-fse' },
                    { label: { en: 'Commercial Costs', pt: 'Custos de Comercialização' }, path: '/finance', section: 'opex-commercial' },
                  ]
                },
                { label: { en: 'EBITDA', pt: 'EBITDA' }, path: '/finance', section: 'ebitda-detail' },
                { label: { en: 'Non-Operating Results', pt: 'Resultados Não Operacionais' }, path: '/finance', section: 'non-operating' },
                { label: { en: 'Financial Results', pt: 'Resultados Financeiros' }, path: '/finance', section: 'financial-results' },
                { label: { en: 'Industrial Tax', pt: 'Imposto Industrial' }, path: '/finance', section: 'industrial-tax' },
                { label: { en: 'Net Income', pt: 'Resultado Líquido' }, path: '/finance', section: 'netincome-detail' },
                { label: { en: 'Unit Cost per Carat', pt: 'Custo Unitário por Quilate' }, path: '/finance', section: 'costpercarat-detail' },
              ]
            },
            { label: { en: 'Liquidity & Cash Generation', pt: 'Liquidez e Geração de Caixa' }, path: '/finance', section: 'liquidity' },
            { label: { en: 'Balance Sheet', pt: 'Balanço Patrimonial' }, path: '/finance', section: 'balance-sheet' },
            { label: { en: 'Debt & Financing', pt: 'Dívida e Financiamento' }, path: '/finance', section: 'debt' },
            { label: { en: 'CAPEX', pt: 'CAPEX' }, path: '/finance', section: 'capex' },
          ]
        },
        { 
          label: { en: 'Multi-Period Comparative', pt: 'Comparativo Multi-Período' }, 
          path: '/finance', 
          section: 'multi-period-comparative'
        },
        { 
          label: { en: 'Reports & Institutional Docs', pt: 'Relatórios e Documentação Institucional' }, 
          path: '/finance', 
          section: 'reports',
          hasSubmenu: true,
          submenu: [
            { label: { en: 'Auditor Report', pt: 'Relatório do Auditor' }, path: '/finance', section: 'external-reports' },
            { label: { en: 'Financial Reports', pt: 'Relatórios Financeiros' }, path: '/finance', section: 'internal-docs' },
            { label: { en: 'Platform Generated Reports', pt: 'Relatórios Gerados pela Plataforma' }, path: '/finance', section: 'platform-reports' },
          ]
        },
      ],
    },
    hr: {
      label: { en: 'Human Resources', pt: 'Recursos Humanos' },
      disabled: true,
      items: [],
    },
    esg: {
      label: { en: 'ESG', pt: 'ESG' },
      disabled: true,
      items: [],
    },
  };

  return (
    <header className="bg-slate-50" data-testid="executive-header">
      <div className="max-w-[1600px] mx-auto px-8 py-3">
        <div className="flex items-center justify-between">
          {/* Logo and Platform Name */}
          <div 
            className="flex items-center gap-4 cursor-pointer" 
            onClick={() => navigate('/')}
          >
            {/* riicu.ai Logo - Text styled like the official website */}
            <span className="inline-flex items-end leading-none">
              <span className="text-[1.85rem] font-bold tracking-[-0.05em] text-slate-800">riicu</span>
              <span className="mx-[0.08em] text-[2.05rem] font-bold leading-[0.72] text-blue-600">.</span>
              <span className="text-[1.45rem] font-medium tracking-[-0.04em] text-blue-400">ai</span>
            </span>
            <div className="hidden lg:block">
              <h1 className="text-sm font-semibold text-primary leading-tight">
                {PLATFORM_NAME[language]}
              </h1>
            </div>
          </div>

          {/* Navigation Dropdowns */}
          <nav className="flex items-center gap-8">
            {Object.entries(menuItems).map(([key, menu]) => (
              menu.disabled ? (
                // Disabled menu items (HR, ESG) - no dropdown, no navigation
                <div 
                  key={key}
                  className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-slate-400 cursor-default leading-none"
                  title={language === 'pt' ? 'Módulo Previsto' : 'Planned Module'}
                >
                  {menu.label[language]}
                </div>
              ) : (
              <DropdownMenu key={key}>
                <DropdownMenuTrigger className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-primary hover:text-gold-600 transition-colors outline-none leading-none">
                  {menu.label[language]}
                  <ChevronDown className="w-4 h-4" />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="min-w-[220px]">
                  {menu.items.map((item, idx) => (
                    item.hasSubmenu ? (
                      <DropdownMenuSub key={idx}>
                        <DropdownMenuSubTrigger className="cursor-pointer">
                          {item.label[language]}
                        </DropdownMenuSubTrigger>
                        <DropdownMenuSubContent className="min-w-[220px]">
                          {item.submenu.map((subItem, subIdx) => (
                            subItem.hasSubmenu ? (
                              <DropdownMenuSub key={subIdx}>
                                <DropdownMenuSubTrigger className="cursor-pointer">
                                  {subItem.label[language]}
                                </DropdownMenuSubTrigger>
                                <DropdownMenuSubContent className="min-w-[200px]">
                                  {subItem.submenu.map((subSubItem, subSubIdx) => (
                                    subSubItem.hasSubmenu ? (
                                      <DropdownMenuSub key={subSubIdx}>
                                        <DropdownMenuSubTrigger className="cursor-pointer">
                                          {subSubItem.label[language]}
                                        </DropdownMenuSubTrigger>
                                        <DropdownMenuSubContent className="min-w-[200px]">
                                          {subSubItem.submenu.map((level4Item, level4Idx) => (
                                            <DropdownMenuItem 
                                              key={level4Idx}
                                              onClick={() => {
                                                const url = level4Item.section ? `${level4Item.path}?section=${level4Item.section}` : level4Item.path;
                                                navigate(url);
                                              }}
                                              className="cursor-pointer"
                                            >
                                              {level4Item.label[language]}
                                            </DropdownMenuItem>
                                          ))}
                                        </DropdownMenuSubContent>
                                      </DropdownMenuSub>
                                    ) : (
                                      <DropdownMenuItem 
                                        key={subSubIdx}
                                        onClick={() => {
                                          const url = subSubItem.section ? `${subSubItem.path}?section=${subSubItem.section}` : subSubItem.path;
                                          navigate(url);
                                        }}
                                        className="cursor-pointer"
                                      >
                                        {subSubItem.label[language]}
                                      </DropdownMenuItem>
                                    )
                                  ))}
                                </DropdownMenuSubContent>
                              </DropdownMenuSub>
                            ) : (
                              <DropdownMenuItem 
                                key={subIdx}
                                onClick={() => {
                                  const url = subItem.section ? `${subItem.path}?section=${subItem.section}` : subItem.path;
                                  navigate(url);
                                }}
                                className="cursor-pointer"
                              >
                                {subItem.label[language]}
                              </DropdownMenuItem>
                            )
                          ))}
                        </DropdownMenuSubContent>
                      </DropdownMenuSub>
                    ) : (
                      <DropdownMenuItem 
                        key={idx}
                        onClick={() => {
                          const url = item.section ? `${item.path}?section=${item.section}` : item.path;
                          navigate(url);
                        }}
                        className="cursor-pointer"
                      >
                        {item.label[language]}
                      </DropdownMenuItem>
                    )
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
              )
            ))}
          </nav>

          {/* Language Toggle */}
          <button
            onClick={toggleLanguage}
            className="flex items-center gap-2 px-3 py-1.5 bg-primary/10 rounded hover:bg-primary/20 transition-colors text-primary"
            data-testid="language-toggle"
          >
            <Globe className="w-4 h-4" />
            <span className="text-sm font-medium uppercase">{language}</span>
          </button>
        </div>
      </div>
    </header>
  );
};

export default ExecutiveHeader;
